export type { CaptureCurrent, PriostahlRecaptureContext, PriostahlRecaptureWireInput, RecaptureConflictEnvelope } from './priostahl-recapture'
// stahlia-frontend/src/api/warehouseMigration.ts
// API-Layer Erstaufnahme & Alt-System-Migration (Modul ⑧, Spec §6/§10).
// Alle Pfade relativ zur BASE '/api' (client.ts). Backend: src/modules/warehouse/migration.ts.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiFetch, isTransportError } from './client'
import { useWarehouseCompanyScope } from '@/lib/warehouse-company'

export interface MigrationRowProgress {
  rowCode: string; total: number
  aufgenommen: number; leer: number; offen: number; abweichung: number
  complete: boolean
}
export interface MigrationTotals {
  total: number; aufgenommen: number; leer: number; offen: number; abweichung: number
}
export interface MigrationProgress {
  rows: MigrationRowProgress[]
  totals: MigrationTotals
  offenByRow: Record<string, string[]>
}

export function useMigrationProgress() {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'migration', 'progress', scope.key],
    queryFn: () => apiFetch<MigrationProgress>('/warehouse/migration/progress/'),
    refetchInterval: 30_000,
  })
}

export interface Abweichung {
  placeBarcode: string; expectedArtikelRaw: string | null; infoText: string | null
  erledigtByName: string | null; erledigtAt: string | null
}

export function useAbweichungen() {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'migration', 'abweichungen', scope.key],
    queryFn: () => apiFetch<{ abweichungen: Abweichung[] }>('/warehouse/migration/abweichungen/').then((r) => r.abweichungen),
  })
}

export function useGenerateLabels() {
  const qc = useQueryClient()
  return useMutation({
    // RA6-WEB-3: Pflicht-idempotency_key — die Page haelt den Key ueber ambivalente
    // Transportfehler hinweg fest; das Backend replayed denselben Batch (Migration 0085).
    mutationFn: (body: { row_code: string; count: number; idempotency_key: string }) =>
      apiFetch<{ paletteNos: string[]; batchId?: number; duplicate?: boolean }>('/warehouse/migration/labels/generate/', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'progress'] })
      // Neuer Druckauftrag muss sofort in der bereits gemounteten Auftragsliste erscheinen (Codex 2026-07-14).
      void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'batches'] })
    },
    // Fehler-Anzeige macht die Page (Transport-ambivalent vs. klar getrennt) — kein Doppel-Toast hier.
  })
}

export function useReleaseRow() {
  const qc = useQueryClient()
  const refresh = () => void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'progress'] })
  return useMutation({
    mutationFn: (rowCode: string) =>
      apiFetch<{ released: true }>(`/warehouse/migration/row/${encodeURIComponent(rowCode)}/release/`, {
        method: 'POST', body: JSON.stringify({}),
      }),
    onSuccess: refresh,
    // RA6-WEB-6: ambivalente Transportfehler NICHT wie klare Ablehnungen behandeln — die
    // Freigabe kann serverseitig durch sein (Antwort verloren). Ansicht aktualisieren statt
    // blind erneut zu senden (Doppel-Journal).
    onError: (e) => {
      if (isTransportError(e)) {
        toast.warning('Verbindung unklar — Freigabe wurde möglicherweise ausgeführt. Ansicht wird aktualisiert.')
        refresh()
      } else {
        toast.error(e instanceof Error ? e.message : 'Regal-Freigabe fehlgeschlagen')
      }
    },
  })
}

/** Leerplätze einer Reihe per Leiter-Klick als `leer` bestätigen (ohne Scanner, User-Entscheid 2026-07-14). */
export function useConfirmEmptyPlaces() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (rowCode: string) =>
      apiFetch<{ confirmed: number; skipped_occupied: number }>(`/warehouse/migration/row/${encodeURIComponent(rowCode)}/confirm-empty/`, {
        method: 'POST', body: JSON.stringify({}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'progress'] })
      // RA6-WEB-5: ein bereits geoeffnetes Kontroll-Raster zeigt die bestaetigten Plaetze sonst
      // bis zum Fokuswechsel als 'offen'.
      void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'overview'] })
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Leerplatz-Bestätigung fehlgeschlagen'),
  })
}

export interface AbgleichRef {
  raw: string
  paletteNo: string | null
  placeBarcode: string | null
  found: boolean
  currentState: string | null
  matchedBy: 'palette' | 'place' | null
  candidateCount: number
}

export function useAbgleichPreview() {
  return useMutation({
    mutationFn: (rawInput: string) =>
      apiFetch<{ refs: AbgleichRef[]; unmatched: string[] }>('/warehouse/migration/abgleich/preview/', {
        method: 'POST', body: JSON.stringify({ raw_input: rawInput }),
      }),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Vorschau fehlgeschlagen'),
  })
}

export function useAbgleichCommit() {
  const qc = useQueryClient()
  return useMutation({
    // RA6-WEB-2: Pflicht-idempotency_key — die Page haelt den Key an den Vorschau-Snapshot
    // gebunden und wiederholt bei ambivalentem Transportfehler mit DEMSELBEN Key; das
    // Backend replayed das Original-Summary (Migration 0085). Ohne das konnte ein Retry
    // am selben Platz die NAECHSTE verschlossene Palette als ausgepackt markieren.
    mutationFn: (body: { raw_input: string; idempotency_key: string }) =>
      apiFetch<{ updated: number; unmatched: number; duplicate?: boolean }>('/warehouse/migration/abgleich/commit/', {
        method: 'POST', body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'progress'] })
      void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'abweichungen'] })
    },
    // Fehler-Anzeige macht die Page (Transport-ambivalent = Sperre + Retry mit gleichem Key).
  })
}

// ---------- Druckaufträge (persistent + Storno, Migration 0081) ----------
export interface LabelBatch {
  id: number; rowCode: string; labelCount: number; status: string
  createdByName: string | null; createdAt: string | null
  cancelledByName: string | null; cancelledAt: string | null
  paletteNos: string[]
  /** Erfassungs-Kontrolle: bereits per Scan-App gebuchte Etiketten dieses Auftrags (booked). */
  capturedCount: number
}

export function useLabelBatches() {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'migration', 'batches', scope.key],
    queryFn: () => apiFetch<{ batches: LabelBatch[] }>('/warehouse/migration/batches/').then((r) => r.batches),
    // RA6-WEB-5: Erfassungs-Zaehler (x/y erfasst) aendern sich durch Scan-App-Buchungen —
    // ohne Polling blieb die geoeffnete Liste bis zum Tab-/Fokuswechsel veraltet.
    refetchInterval: 15_000,
  })
}

export function useCancelBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ cancelled: boolean; freed: number }>(`/warehouse/migration/batches/${id}/cancel/`, {
        method: 'POST', body: JSON.stringify({}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'batches'] })
      void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'progress'] })
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Storno fehlgeschlagen'),
  })
}

// ---------- Digitale Regal-Kontrolle (interaktives Raster) ----------
export interface ShelfCell {
  occupied?: boolean
  displayState?: 'recapture_pending'
  paletteNo?: string | null
  placeBarcode: string; sternnummer: string | null; status: string
  /** Erfasste Menge + Positions-ID (nur bei status 'aufgenommen'): Tooltip + Doppelklick-Korrektur. */
  qty: number | null; positionId: number | null
  /** Packungsgroesse der Erfassung — Vorbefuellung des Kartons x Stueck-Editors. */
  piecesPerCarton: number | null
  /** Zustand der Position — entscheidet, welches Mengen-Feld die Korrektur beschreibt (P1). */
  positionState: string | null
}
export interface CorrectQtyInput {
  qty: number
  piecesPerCarton?: number
  reason: string
}

/** Baut den Rumpf der Mengen-Korrektur: NUR die Menge, nicht das Zielfeld.
 *
 *  P1 (Welle 1b), Prod-Vorfall 2026-07-26: Vorher ging IMMER `qty_exact` raus. Bei einer
 *  geoeffneten Palette ist das die historische Erstaufnahme-Zaehlung, waehrend `qty_circa`
 *  den laufenden Stand fuehrt — die Korrektur meldete Erfolg, schrieb eine Journal-Zeile
 *  „600 -> 700" und liess den Wert, mit dem der Pick rechnet, unveraendert auf 600.
 *
 *  P1-Nachschliff (Codex-Befund 1): Die erste Fassung liess den CLIENT das Feld anhand
 *  des Raster-Zustands waehlen. Das Raster ist aber ein Schnappschuss (Refetch alle 15 s,
 *  Editor kann beliebig lange offen stehen) — packt jemand die Palette in der Zwischenzeit
 *  aus, waehlt der Client wieder das tote Feld und der Defekt ist zurueck. Das Zielfeld
 *  bestimmt jetzt der Server aus dem FOR-UPDATE-gesperrten Zustand
 *  (`massgeblichesMengenFeld()` in fillup-logic.ts, aufgerufen in
 *  `service.correctPositionQty`). Der Client schickt nur noch `qty`. */
export function buildCorrectQtyBody(a: CorrectQtyInput): Record<string, unknown> {
  return {
    qty: a.qty,
    ...(a.piecesPerCarton ? { pieces_per_carton: a.piecesPerCarton } : {}),
    reason: a.reason,
  }
}

export interface ShelfOverviewData {
  rowCode: string
  columns: Array<{ fieldNo: number; slotNo: number; label: string }>
  levels: number[]
  cell: Record<string, ShelfCell>
  occupiedCount: number; leerCount: number
}

export function useShelfOverview(rowCode: string | null) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'migration', 'overview', rowCode, scope.key],
    queryFn: () => apiFetch<ShelfOverviewData>(`/warehouse/migration/row/${encodeURIComponent(rowCode as string)}/overview/`),
    enabled: !!rowCode,
    // RA6-WEB-5: Scan-App-Erfassungen sollen im offenen Kontroll-Raster zeitnah gruen werden.
    refetchInterval: 15_000,
  })
}

/** Einzelplatz-Aktionen des interaktiven Rasters: leer bestätigen / Abweichung / zurücksetzen. */
export function usePlaceActions() {
  const qc = useQueryClient()
  const onSuccess = () => {
    void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'overview'] })
    void qc.invalidateQueries({ queryKey: ['warehouse', 'migration', 'progress'] })
  }
  const post = (path: string) => (placeBarcode: string) =>
    apiFetch(path, { method: 'POST', body: JSON.stringify({ place_barcode: placeBarcode }) })
  const empty = useMutation({
    mutationFn: post('/warehouse/migration/place-empty/'), onSuccess,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Leer-Meldung fehlgeschlagen'),
  })
  const discrepancy = useMutation({
    mutationFn: post('/warehouse/migration/place-discrepancy/'), onSuccess,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Abweichung fehlgeschlagen'),
  })
  const reset = useMutation({
    mutationFn: post('/warehouse/migration/place-reset/'), onSuccess,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Zurücksetzen fehlgeschlagen'),
  })
  // Mengen-Korrektur per Doppelklick (User-Wunsch 2026-07-15): nutzt die bestehende
  // Leiter-Route /position/:id/correct/ (correct_qty-Audit-Move im Journal).
  // Eingabe wie bei der Erfassung: Kartons x Stueck/Karton — die Packungsgroesse
  // wird mitkorrigiert (pieces_per_carton), damit sie zur neuen Menge passt.
  // Feldwahl macht der Server aus dem gesperrten Zustand — siehe buildCorrectQtyBody (P1).
  const correctQty = useMutation({
    mutationFn: (a: CorrectQtyInput & { positionId: number }) =>
      apiFetch(`/warehouse/position/${a.positionId}/correct/`, {
        method: 'POST', body: JSON.stringify(buildCorrectQtyBody(a)),
      }),
    onSuccess: () => {
      onSuccess()
      toast.success('Menge korrigiert')
    },
    // RA6-WEB-6: ambivalenter Transportfehler = Korrektur moeglicherweise gebucht (Antwort
    // verloren) — Ansicht aktualisieren und den Ist-Stand zeigen statt blind erneut zu senden
    // (die Korrektur setzt absolute Werte, aber jeder Resend schreibt eine weitere
    // correct_qty-Journalzeile).
    onError: (e) => {
      if (isTransportError(e)) {
        toast.warning('Verbindung unklar — Korrektur wurde möglicherweise gespeichert. Ansicht wird aktualisiert, bitte Wert prüfen.')
        onSuccess()
      } else {
        toast.error(e instanceof Error ? e.message : 'Mengen-Korrektur fehlgeschlagen')
      }
    },
  })
  return { empty, discrepancy, reset, correctQty }
}

/** RA6-WEB (Minor): ACK-Fehlertext aus einer non-OK-PDF-Antwort ziehen — ein 404 nannte sonst
 *  nur den HTTP-Code statt der konkreten fehlenden Paletten/Bindungen. */
async function pdfError(res: Response): Promise<Error> {
  try {
    const j = await res.json() as { error?: Array<{ message?: string }> }
    const msg = j.error?.[0]?.message
    if (msg) return new Error(msg)
  } catch { /* non-JSON body -> generisch */ }
  return new Error(`PDF fehlgeschlagen (HTTP ${res.status})`)
}

/** Regal-Übersicht-PDF (Leiter-Kontrolle ohne Scanner) in neuem Tab öffnen. GET je Regal-Code. */
export async function openShelfOverviewPdf(rowCode: string): Promise<void> {
  const token = localStorage.getItem('stahlia_token')
  const res = await fetch(`/api/warehouse/migration/row/${encodeURIComponent(rowCode)}/overview-pdf/`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  if (!res.ok) throw await pdfError(res)
  window.open(URL.createObjectURL(await res.blob()), '_blank')
}

/** Vorbelegte Etiketten-PDF (Front-A4 oder Seiten-100x150) in neuem Tab oeffnen — bewusst kein
 *  erzwungener Download wie `downloadPdf` in api/warehouse.ts (Spec §6②: "in neuem Tab oeffnen"). */
export async function openVorbelegtLabelsPdf(kind: 'front' | 'side', paletteNos: string[]): Promise<void> {
  const token = localStorage.getItem('stahlia_token')
  const res = await fetch(`/api/warehouse/migration/labels/${kind}-pdf/`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ palette_nos: paletteNos }),
  })
  if (!res.ok) throw await pdfError(res)
  window.open(URL.createObjectURL(await res.blob()), '_blank')
}
